import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Assessment,
  Paginated,
  ProgressSummary,
  Standard,
  StandardVersion,
} from '@grc/types';
import { apiFetch, ApiRequestError } from '../../lib/api.js';
import { useOrg } from '../../lib/auth.js';
import {
  Badge,
  Button,
  Card,
  Dialog,
  EmptyState,
  Field,
  Input,
  LoadingPane,
  PageHeader,
  ProgressBar,
  Select,
  formatDate,
  useToast,
} from '../../components/ui.js';
import './assessments.css';

export function AssessmentsPage() {
  const { org } = useOrg();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isAdmin = org.role === 'admin';

  const assessments = useQuery({
    queryKey: ['assessments', org.orgId],
    queryFn: () => apiFetch<Assessment[]>(`/orgs/${org.orgId}/assessments`),
  });

  const progressQueries = useQueries({
    queries: (assessments.data ?? []).map((a) => ({
      queryKey: ['progress', a.id],
      queryFn: () => apiFetch<ProgressSummary>(`/assessments/${a.id}/progress`),
    })),
  });
  const progressById = new Map<string, ProgressSummary>();
  (assessments.data ?? []).forEach((a, i) => {
    const p = progressQueries[i]?.data;
    if (p) progressById.set(a.id, p);
  });

  // New-assessment dialog state
  const [open, setOpen] = useState(false);
  const [standardId, setStandardId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [name, setName] = useState('');
  const [targetDate, setTargetDate] = useState('');

  const standards = useQuery({
    queryKey: ['standards'],
    queryFn: () => apiFetch<Paginated<Standard>>('/standards', { query: { limit: 100 } }),
    enabled: open,
  });
  const versions = useQuery({
    queryKey: ['standard-versions', standardId],
    queryFn: () => apiFetch<StandardVersion[]>(`/standards/${standardId}/versions`),
    enabled: !!standardId,
  });

  const create = useMutation({
    mutationFn: () =>
      apiFetch<Assessment>(`/orgs/${org.orgId}/assessments`, {
        method: 'POST',
        body: {
          standardVersionId: versionId,
          name: name.trim(),
          targetDate: targetDate || null,
        },
      }),
    onSuccess: (created) => {
      void queryClient.invalidateQueries({ queryKey: ['assessments', org.orgId] });
      toast('Assessment created with one response per question.', 'success');
      navigate(`/assessments/${created.id}`);
    },
    onError: (err) =>
      toast(err instanceof ApiRequestError ? err.message : 'Could not create the assessment', 'error'),
  });

  if (assessments.isLoading) return <LoadingPane label="Loading assessments" />;

  const chooseVersion = (id: string) => {
    setVersionId(id);
    const version = versions.data?.find((v) => v.id === id);
    if (version && !name.trim()) setName(`${version.label} assessment`);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (versionId && name.trim()) create.mutate();
  };

  return (
    <div>
      <PageHeader
        title="Assessments"
        subtitle="Certification cycles for your organisation"
        actions={isAdmin && <Button onClick={() => setOpen(true)}>New assessment</Button>}
      />

      {(assessments.data ?? []).length === 0 ? (
        <EmptyState
          title="No assessments yet"
          message={
            isAdmin
              ? 'Create your first assessment to generate a workspace of questions from a standard.'
              : 'No assessments have been created for this organisation yet.'
          }
          action={isAdmin ? <Button onClick={() => setOpen(true)}>Create your first assessment</Button> : undefined}
        />
      ) : (
        <div className="assessment-grid">
          {(assessments.data ?? []).map((a) => {
            const progress = progressById.get(a.id);
            return (
              <Card key={a.id} className="assessment-card">
                <h3>
                  <Link to={`/assessments/${a.id}`}>{a.name}</Link>
                </h3>
                <div className="meta">
                  <Badge tone="primary">{a.standardName ?? 'Standard'}</Badge>
                  <Badge>{a.versionLabel}</Badge>
                  <Badge tone={a.status === 'open' ? 'info' : a.status === 'locked' ? 'neutral' : 'warning'}>
                    {a.status}
                  </Badge>
                  {a.targetDate && <span>target {formatDate(a.targetDate)}</span>}
                </div>
                {progress && (
                  <>
                    <div className="progress-row">
                      <ProgressBar
                        percent={progress.completionPercent}
                        label={`Completion of ${a.name}`}
                      />
                      <strong>{progress.completionPercent}%</strong>
                    </div>
                    <div className="meta" style={{ marginTop: 8 }}>
                      <span>{progress.total} questions</span>
                      {progress.evidenceMissing > 0 && (
                        <Badge tone="warning">{progress.evidenceMissing} evidence missing</Badge>
                      )}
                      {progress.openReviewerComments + progress.openAssessorComments > 0 && (
                        <Badge tone="danger">
                          {progress.openReviewerComments + progress.openAssessorComments} open comments
                        </Badge>
                      )}
                      {progress.exportReady && <Badge tone="success">export ready</Badge>}
                    </div>
                  </>
                )}
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onClose={() => setOpen(false)} title="New assessment">
        <form onSubmit={submit}>
          <Field label="Standard" required>
            {(props) => (
              <Select
                {...props}
                required
                value={standardId}
                onChange={(e) => {
                  setStandardId(e.target.value);
                  setVersionId('');
                }}
              >
                <option value="">Choose a standard…</option>
                {(standards.data?.items ?? []).map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Version" required>
            {(props) => (
              <Select
                {...props}
                required
                disabled={!standardId}
                value={versionId}
                onChange={(e) => chooseVersion(e.target.value)}
              >
                <option value="">Choose a version…</option>
                {(versions.data ?? []).map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.label}
                    {v.status !== 'active' ? ` (${v.status})` : ''}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label="Assessment name" required>
            {(props) => (
              <Input {...props} required value={name} onChange={(e) => setName(e.target.value)} />
            )}
          </Field>
          <Field label="Target date" hint="Optional">
            {(props) => (
              <Input
                {...props}
                type="date"
                value={targetDate}
                onChange={(e) => setTargetDate(e.target.value)}
              />
            )}
          </Field>
          <div className="dialog-actions">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" loading={create.isPending} disabled={!versionId || !name.trim()}>
              Create assessment
            </Button>
          </div>
        </form>
      </Dialog>
    </div>
  );
}
